<template>
  <div class="mnemonic-input-container">
    <div class="content">
      <h1 class="title">{{ $t('mnemonicInput.title') }}</h1>
      
      <p class="description">
        {{ $t('mnemonicInput.description') }}
      </p>
      
      <a href="#" class="learn-link" @click.prevent="handleLearnMore">{{ $t('mnemonicInput.learnLink') }}</a>
      
      <div class="input-wrapper">
        <textarea 
          v-model="mnemonicInput"
          class="mnemonic-textarea"
          :placeholder="$t('mnemonicInput.placeholder')"
          @input="handleInput"
        ></textarea>
        
        <!-- 输入框下方的错误提示 -->
        <transition name="error-fade">
          <div v-if="showError" class="error-message">
            {{ errorMessage }}
          </div>
        </transition>
      </div>
    </div>
    
    <div class="footer">
      <button 
        class="submit-button" 
        :class="{ 'disabled': !isValid }"
        :disabled="!isValid"
        @click="handleSubmit"
      >
        {{ $t('mnemonicInput.importNow') }}
      </button>
    </div>

    <!-- 底部弹窗 -->
    <transition name="modal">
      <div v-if="showModal" class="modal-overlay" @click="closeModal">
        <div class="modal-content" @click.stop>
          <button class="modal-close" @click="closeModal">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" fill="#d0d0d0"/>
              <path d="M15 9L9 15M9 9L15 15" stroke="white" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
          
          <h2 class="modal-title">{{ $t('mnemonicInput.modalTitle') }}</h2>
          
          <div class="modal-body">
            <p class="modal-text">{{ $t('mnemonicInput.modalP1') }}</p>
            <p class="modal-text">{{ $t('mnemonicInput.modalP2') }}</p>
            
            <ul class="modal-list">
              <li>{{ $t('mnemonicInput.modalL1') }}</li>
              <li>{{ $t('mnemonicInput.modalL2') }}</li>
              <li>{{ $t('mnemonicInput.modalL3') }}</li>
              <li>{{ $t('mnemonicInput.modalL4') }}</li>
            </ul>
          </div>
          
          <button class="modal-button" @click="handleLearnMoreLink">
            {{ $t('mnemonicInput.learnMore') }}
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { validateMnemonic } from '../utils/mnemonicValidator'
import {
  importMnemonicAfterValidation,
  closeWebViewAfterImport,
  closeIfMnemonicAlreadySaved,
  getMnemonicErrorMessage
} from '../utils/mnemonicImport'

export default {
  name: 'MnemonicInput',
  data() {
    return {
      mnemonicInput: '',
      isValid: false,
      validationResult: null,
      showError: false,
      errorMessage: '',
      showModal: false
    }
  },
  mounted() {
    closeIfMnemonicAlreadySaved(this, this.$route);
  },
  methods: {
    handleInput() {
      // 使用 BIP39 单词列表验证助记词
      const result = validateMnemonic(this.mnemonicInput);
      this.validationResult = result;
      
      // 只有当所有单词都有效且数量正确时才启用按钮
      this.isValid = result.valid;
      
      // 检查是否有无效单词
      if (result.wordCount > 0) {
        const invalidWords = result.results.filter(r => !r.valid);
        
        if (invalidWords.length > 0) {
          const invalidWordsList = invalidWords.map(w => w.word).join(', ');
          this.showError = true;
          this.errorMessage = this.$t('errors.invalidWords', { words: invalidWordsList });
        } else if (!result.valid && result.error === 'invalid_checksum') {
          this.showError = true;
          this.errorMessage = this.$t('errors.checksumInvalid');
        } else if (!result.valid && result.error === 'invalid_length') {
          this.showError = true;
          this.errorMessage =
            result.wordCount > 0 && result.wordCount < 12
              ? this.$t('errors.incompleteWords', { count: result.wordCount })
              : this.$t('errors.invalidLength', { count: result.wordCount });
        } else {
          this.showError = false;
          this.errorMessage = '';
        }
      } else {
        // 输入为空时隐藏错误
        this.showError = false;
        this.errorMessage = '';
      }
      
      // 在控制台显示验证结果（可选，用于调试）
      if (result.wordCount > 0) {
        console.log(`Validation result: ${result.validWords}/${result.wordCount} words are valid`);
        
        // Show invalid word suggestions
        result.results.forEach((wordResult) => {
          if (!wordResult.valid && wordResult.suggestions.length > 0) {
            console.log(`Possible spelling for "${wordResult.word}": ${wordResult.suggestions.join(', ')}`);
          }
        });
      }
    },
    handleLearnMore() {
      // 打开底部弹窗
      this.showModal = true;
    },
    closeModal() {
      // 关闭弹窗
      this.showModal = false;
    },
    handleLearnMoreLink() {
      // 点击"了解更多"按钮，跳转到帮助页面
      window.open('https://support.token.im/hc/en-us/articles/360002074233-What-is-a-mnemonic-and-how-to-restore-a-wallet-with-it', '_blank');
      this.closeModal();
    },
    async handleSubmit() {
      if (!this.isValid) return;

      const outcome = await importMnemonicAfterValidation(
        this,
        this.mnemonicInput,
        this.$route
      );

      if (outcome.ok) {
        closeWebViewAfterImport(this);
      } else {
        this.showError = true;
        this.errorMessage =
          outcome.message || getMnemonicErrorMessage(this, outcome.local);
      }
    }
  }
}
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.mnemonic-input-container {
  min-height: 100vh;
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
  position: relative;
}

.content {
  flex: 1;
  padding: 20px 24px 0 24px;
  overflow-y: auto;
}

.title {
  font-size: 32px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 16px 0;
  text-align: left;
  letter-spacing: -0.5px;
}

.description {
  font-size: 15px;
  color: #666;
  line-height: 1.6;
  margin: 0 0 12px 0;
  text-align: left;
}

.learn-link {
  display: inline-block;
  font-size: 15px;
  color: #007aff;
  text-decoration: none;
  margin-bottom: 24px;
  font-weight: 500;
  transition: opacity 0.2s ease;
}

.learn-link:active {
  opacity: 0.6;
}

.input-wrapper {
  margin-bottom: 24px;
  position: relative;
}

.mnemonic-textarea {
  width: 100%;
  height: 320px;
  padding: 20px;
  font-size: 16px;
  color: #1a1a1a;
  background: white;
  border: none;
  border-radius: 24px;
  resize: none;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
  line-height: 1.5;
  transition: all 0.2s ease;
  outline: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.mnemonic-textarea::placeholder {
  color: #b0b0b0;
  font-size: 15px;
}

.mnemonic-textarea:focus {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

/* 输入框下方的错误提示 */
.error-message {
  margin-top: 12px;
  padding: 0 4px;
  font-size: 14px;
  color: #e57373;
  line-height: 1.4;
  font-weight: 600;
}

/* 错误提示动画 */
.error-fade-enter-active,
.error-fade-leave-active {
  transition: all 0.3s ease;
}

.error-fade-enter-from {
  opacity: 0;
  transform: translateY(-8px);
}

.error-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* 底部按钮区域 */
.footer {
  padding: 16px 24px 32px 24px;
  background: #f5f5f5;
}

.submit-button {
  width: 100%;
  background: linear-gradient(135deg, #a8c7f5 0%, #b8d4f8 100%);
  color: white;
  border: none;
  border-radius: 14px;
  padding: 18px;
  font-size: 18px;
  font-weight: 600;
  cursor: not-allowed;
  transition: all 0.3s ease;
  box-shadow: 0 2px 12px rgba(168, 199, 245, 0.3);
  letter-spacing: 0.5px;
}

.submit-button:not(.disabled) {
  background: linear-gradient(135deg, #4a90e2 0%, #5ba3f5 100%);
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(74, 144, 226, 0.4);
}

.submit-button:not(.disabled):active {
  transform: scale(0.98);
  box-shadow: 0 2px 8px rgba(74, 144, 226, 0.3);
}

.submit-button.disabled {
  opacity: 1;
}

/* 移动端适配 */
@media (max-width: 480px) {
  .header {
    padding: 8px 12px;
  }

  .content {
    padding: 16px 20px 0 20px;
  }
  
  .title {
    font-size: 28px;
    margin-bottom: 12px;
  }
  
  .description {
    font-size: 14px;
    margin-bottom: 10px;
  }

  .learn-link {
    font-size: 14px;
    margin-bottom: 20px;
  }
  
  .mnemonic-textarea {
    height: 280px;
    padding: 16px;
    font-size: 15px;
    border-radius: 20px;
  }

  .mnemonic-textarea::placeholder {
    font-size: 14px;
  }
  
  .footer {
    padding: 12px 20px 24px 20px;
  }

  .submit-button {
    padding: 16px;
    font-size: 17px;
    border-radius: 12px;
  }
}

/* 超小屏幕适配 */
@media (max-width: 375px) {
  .title {
    font-size: 26px;
  }

  .mnemonic-textarea {
    height: 280px;
  }
}

/* 底部弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 24px 24px 0 0;
  padding: 32px 24px 40px 24px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
  animation: slideUp 0.3s ease-out;
}

.modal-close {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease;
}

.modal-close:active {
  opacity: 0.6;
}

.modal-title {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 24px 0;
  padding-right: 40px;
}

.modal-body {
  margin-bottom: 32px;
}

.modal-text {
  font-size: 15px;
  color: #666;
  line-height: 1.6;
  margin: 0 0 16px 0;
}

.modal-list {
  list-style: none;
  padding: 0;
  margin: 16px 0 0 0;
}

.modal-list li {
  font-size: 15px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 12px;
  padding-left: 20px;
  position: relative;
}

.modal-list li:before {
  content: "•";
  position: absolute;
  left: 0;
  color: #666;
  font-weight: bold;
}

.modal-button {
  width: 100%;
  background: white;
  color: #1a1a1a;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 16px;
  font-size: 17px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.modal-button:active {
  background: #f5f5f5;
}

/* 弹窗动画 */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .modal-content,
.modal-leave-active .modal-content {
  transition: transform 0.3s ease;
}

.modal-enter-from .modal-content,
.modal-leave-to .modal-content {
  transform: translateY(100%);
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}
</style>

